import { Box, Grid, Stack, Typography } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useAuthStore } from '../../../store/storeAuth';
import { useStoreThirdPartyIntegration } from '../../../store/storeThirdPartyIntegration';
import type { IntegrationCategory } from '../../ThirdPartyIntegration/categories';
import {
  getAllProviders,
  getProvider,
} from '../../ThirdPartyIntegration/registry';
import IntegrationCard from './IntegrationCard';
import IntegrationCategoryChips from './IntegrationCategoryChips';
import IntegrationDetailView from './IntegrationDetailView';
import IntegrationSearchBar from './IntegrationSearchBar';
import { useIntegrationCatalog } from './useIntegrationCatalog';

const IntegrationCatalog: React.FC = () => {
  const { user } = useAuthStore(state => state);
  const isDark = user?.theme === 'dark';
  const fetchConnections = useStoreThirdPartyIntegration(
    state => state.fetchConnections,
  );

  const providers = useMemo(() => getAllProviders(), []);

  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<IntegrationCategory | 'all'>('all');
  const [selectedProviderId, setSelectedProviderId] = useState<string | null>(
    null,
  );

  useEffect(() => {
    providers.forEach(provider => {
      fetchConnections(provider.id);
    });
  }, [providers, fetchConnections]);

  useEffect(() => {
    if (selectedProviderId) {
      window.scrollTo({ top: 0 });
    }
  }, [selectedProviderId]);

  const { connected, browse, totalConnectionsByProvider } =
    useIntegrationCatalog(providers, { search, category });

  const availableCategories = useMemo(() => {
    const set = new Set<IntegrationCategory>();
    for (const provider of providers) {
      for (const item of provider.category ?? []) {
        set.add(item);
      }
    }
    return set;
  }, [providers]);

  const titleColor = isDark ? '#F9FAFB' : '#101828';
  const subtitleColor = isDark ? '#98A2B3' : '#667085';
  const headerBg = isDark ? '#0C111D' : '#FFFFFF';
  const pageBg = isDark ? '#0C111D' : '#F9FAFB';

  const selectedConfig = selectedProviderId
    ? getProvider(selectedProviderId) ?? null
    : null;

  if (selectedConfig) {
    return (
      <Box sx={{ backgroundColor: pageBg, minHeight: '100%' }}>
        <IntegrationDetailView
          config={selectedConfig}
          isDark={isDark}
          onBack={() => setSelectedProviderId(null)}
        />
      </Box>
    );
  }

  const renderEmpty = (label: string) => (
    <Typography
      sx={{
        fontSize: '13px',
        color: subtitleColor,
        py: 2,
      }}
    >
      {label}
    </Typography>
  );

  const renderGrid = (items: typeof connected) => (
    <Grid container spacing={2}>
      {items.map(provider => (
        <Grid key={provider.id} item xs={12} sm={6} md={4} lg={4}>
          <IntegrationCard
            config={provider}
            connectionCount={totalConnectionsByProvider[provider.id] ?? 0}
            isDark={isDark}
            onSelect={setSelectedProviderId}
          />
        </Grid>
      ))}
    </Grid>
  );

  return (
    <Box sx={{ backgroundColor: pageBg, minHeight: '100%' }}>
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 99,
          backgroundColor: headerBg,
          borderBottom: `1px solid ${
            isDark ? 'rgba(71, 84, 103, 0.4)' : '#EAECF0'
          }`,
        }}
      >
        <Box sx={{ px: 3, py: 2.5 }}>
          <Typography
            sx={{
              fontSize: '20px',
              fontWeight: 600,
              color: titleColor,
              mb: 0.5,
            }}
          >
            Integrations
          </Typography>
          <Typography sx={{ fontSize: '13px', color: subtitleColor }}>
            Browse and manage every service you can connect to FluxPrompt.
          </Typography>
        </Box>
      </Box>

      <Box sx={{ px: 3, py: 3 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          alignItems={{ xs: 'stretch', md: 'center' }}
          justifyContent='space-between'
          spacing={2}
          sx={{ mb: 3 }}
        >
          <IntegrationSearchBar
            value={search}
            onChange={setSearch}
            isDark={isDark}
          />
        </Stack>

        <Box sx={{ mb: 3 }}>
          <IntegrationCategoryChips
            selected={category}
            onChange={setCategory}
            availableCategories={availableCategories}
            isDark={isDark}
          />
        </Box>

        {connected.length > 0 && (
          <Box sx={{ mb: 4 }}>
            <Typography
              sx={{
                fontSize: '14px',
                fontWeight: 600,
                color: titleColor,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                mb: 1.5,
              }}
            >
              Connected
            </Typography>
            {renderGrid(connected)}
          </Box>
        )}

        <Box>
          <Typography
            sx={{
              fontSize: '14px',
              fontWeight: 600,
              color: titleColor,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              mb: 1.5,
            }}
          >
            {connected.length > 0 ? 'Browse all' : 'All integrations'}
          </Typography>
          {browse.length > 0
            ? renderGrid(browse)
            : connected.length === 0 &&
              renderEmpty('No integrations match your filters.')}
        </Box>
      </Box>
    </Box>
  );
};

export default IntegrationCatalog;
